import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, RefreshCw } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '../components/Button';
import { supabase } from '../lib/supabase';

type SyncLog = {
  timestamp: string;
  request: any;
  response: any;
  success: boolean;
};

export function SyncMonitor() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Load logs from localStorage
    const savedLogs = localStorage.getItem('syncLogs');
    if (savedLogs) {
      setLogs(JSON.parse(savedLogs));
    }
  }, []);

  const addLog = (log: SyncLog) => {
    setLogs(prevLogs => {
      const newLogs = [log, ...prevLogs].slice(0, 50); // Keep last 50 logs
      localStorage.setItem('syncLogs', JSON.stringify(newLogs));
      return newLogs;
    });
  };

  const clearLogs = () => {
    setLogs([]);
    localStorage.removeItem('syncLogs');
  };

  const handleSync = async (accessToken: string) => {
    setIsLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's tasks
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .eq('completed', false);

      if (tasksError) throw tasksError;

      // Log the request
      const request = {
        userId: user.id,
        tasks: tasks
      };

      // Make the sync request
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-google-tasks`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: user.id,
            accessToken
          })
        }
      );

      const responseData = await response.json();

      // Add log entry
      addLog({
        timestamp: new Date().toISOString(),
        request: {
          ...request,
          accessToken: '[REDACTED]' // Don't log the actual token
        },
        response: responseData,
        success: response.ok
      });

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to sync tasks');
      }

    } catch (error: any) {
      console.error('Error testing sync:', error);
      setError(error.message);
      
      // Log the error
      addLog({
        timestamp: new Date().toISOString(),
        request: { error: 'Failed to make request' },
        response: { error: error.message },
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      if (!response.access_token) {
        setError('No access token received from Google');
        return;
      }
      await handleSync(response.access_token);
    },
    onError: (error) => {
      console.error('Google login error:', error);
      setError('Failed to connect to Google. Please try again.');
      setIsLoading(false);
    },
    scope: 'https://www.googleapis.com/auth/tasks https://www.googleapis.com/auth/tasks.readonly',
    flow: 'implicit'
  });

  const filteredLogs = logs.filter(log => 
    JSON.stringify(log).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#004060]">Sync Monitor</h1>
          <Button
            variant="outline"
            onClick={() => navigate('/tasks')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tasks
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <Button
              onClick={() => login()}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Test Sync
            </Button>
            <Button
              variant="outline"
              onClick={clearLogs}
              disabled={logs.length === 0}
            >
              Clear Logs
            </Button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <div className="relative mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search logs..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>

          <div className="space-y-4">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  log.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                  <span className={`px-2 py-1 rounded text-sm ${
                    log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {log.success ? 'Success' : 'Failed'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Request</h3>
                    <pre className="bg-white p-3 rounded text-sm overflow-auto max-h-60">
                      {JSON.stringify(log.request, null, 2)}
                    </pre>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Response</h3>
                    <pre className="bg-white p-3 rounded text-sm overflow-auto max-h-60">
                      {JSON.stringify(log.response, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}

            {filteredLogs.length === 0 && (
              <p className="text-center text-gray-500 italic">
                {logs.length === 0 ? 'No logs yet' : 'No matching logs found'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}